"use client";

import React, { useState, useEffect } from "react";
import {
  GlossaryCategoryData,
  GlossaryItem,
} from "oddsgate-ds/src/components/organisms/Glossary/Glossary.interface";
import { Glossary } from "oddsgate-ds";
import { WpClientRequest } from "@/lib/wordpress/wpClientRequest";
import { useGlobalContext } from "@/lib/globalContext";
import { useSearchParams } from "next/navigation";
import { ReplaceFrontendUrls } from "@/lib/helpers/replaceFrontendUrl";

export type IBlockGlossary = {
  attrs: {
    blockTitle?: string;
    glossary?: {
      items: GlossaryItem[];
      total: number;
      categories?: GlossaryCategoryData[];
    };
    post_type?: any;
  };
};

export default function BlockGlossary({ attrs }: IBlockGlossary) {
  const { post_type } = attrs || {};
  const [glossary, setGlossary] =
    useState<IBlockGlossary["attrs"]["glossary"]>();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<any[]>([]);

  const params = useSearchParams();
  const searchParam = params.get("search") || "";
  const categoryParam = params.get("category") || "";

  useEffect(() => {
    setSearchTerm(searchParam);
    if (categoryParam && !isNaN(Number(categoryParam))) {
      setSelectedCategory("#");
    } else {
      setSelectedCategory(categoryParam || null);
    }
  }, [searchParam, categoryParam]);

  const state = useGlobalContext();
  const lang = state?.lang;

  useEffect(() => {
    let isCurrent = true;
    WpClientRequest({
      post_type: post_type || "glossary",
      variables: {
        limit: -1,
        lang,
      },
    })
      .then((res) => {
        if (!isCurrent) return;
        if (
          res.categories &&
          Array.isArray(res.categories) &&
          res.categories[0]?.items
        ) {
          setAllCategories(res.categories[0].items);
        }
      })
      .catch((err) => {
        if (!isCurrent) return;
        console.error("Error fetching categories:", err);
      });

    return () => {
      isCurrent = false;
    };
  }, [lang, post_type]);

  // Fetch filtered glossary data
  useEffect(() => {
    let isCurrent = true;

    // Find the category slug from allCategories if selectedCategory exists
    const categorySlug = selectedCategory
      ? allCategories.find((cat) => cat.name === selectedCategory)?.slug
      : undefined;

    const debounce = setTimeout(() => {
      WpClientRequest({
        post_type: post_type || "glossary",
        variables: {
          limit: -1,
          lang,
          search: searchTerm ? searchTerm : undefined,
          category: categorySlug || undefined,
        },
      })
        .then((res) => {
          if (!isCurrent) return;
          setGlossary(res);
        })
        .catch((err) => {
          if (!isCurrent) return;
          console.error(err);
        });
    }, 300);

    return () => {
      clearTimeout(debounce);
      isCurrent = false;
    };
  }, [searchTerm, selectedCategory, lang, post_type, allCategories]);

  const handleCategoryChange = (cat: string | null) => {
    setSelectedCategory(cat === selectedCategory ? null : cat);
    setSearchTerm("");
  };

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    setSelectedCategory(null);
  };

  // Build categoryData map using allCategories
  const categoryData: GlossaryCategoryData = {};
  if (allCategories.length > 0) {
    allCategories.forEach((cat) => {
      categoryData[cat.name] = [];
    });
    if (glossary?.items) {
      glossary.items.forEach((item) => {
        if (
          (item as any).categories &&
          Array.isArray((item as any).categories) &&
          (item as any).categories.length > 0
        ) {
          const itemCategoryItems = (item as any).categories[0]?.items || [];
          itemCategoryItems.forEach((cat: any) => {
            if (categoryData[cat.name]) {
              categoryData[cat.name].push({
                title: item.title,
                slug: ReplaceFrontendUrls(item.slug || ""),
              });
            }
          });
        }
      });
    }
  }

  const categoryNames = Object.keys(categoryData);
  // Move "#" to the first position if it exists
  if (categoryNames.includes("#")) {
    categoryNames.splice(categoryNames.indexOf("#"), 1);
    categoryNames.unshift("#");
  }

  return (
    <>
      {glossary && glossary.items && (
        <Glossary
          searchTerm={searchTerm}
          setSearchTerm={handleSearchTermChange}
          selectedCategory={selectedCategory}
          setSelectedCategory={handleCategoryChange}
          categoryData={categoryData}
          placeholder={
            state?.dict?.glossary?.searchPlaceholder ||
            "What are you looking for?"
          }
          backToTopButton={
            state?.dict?.glossary?.backToTopButton || "Back to Top"
          }
          noResultsText={
            state?.dict?.glossary?.noResultsText || "No results found"
          }
          tryAnotherLetterText={
            state?.dict?.glossary?.tryAnotherLetterText || "Try another letter"
          }
        />
      )}
    </>
  );
}
